// src/components/ReportContainer.js
import React, { useState, useEffect, useMemo } from "react";
import { Text, Icon } from "../custom-components";
import { IcEditPen } from "../custom-components/Icon";
import { IcEditPen, IcChevronDown, IcChevronUp } from "../custom-components/Icon";
import { formatToIST } from "../utils/dateUtils";

/* ===========================
   Styles
   =========================== */
const css = `
.report-container-wrapper {
  display: flex;
  flex-direction: column;
  background: transparent;
  font-family: Inter, "Helvetica Neue", Arial, sans-serif;
  color: #111827;
  width: 100%;
  box-sizing: border-box;
}

.center-wrap { width: 100%; }

/* SUMMARY CARDS */
.summary-cards-container {
  display: flex;
  gap: 16px;
  margin-bottom: 32px;
  flex-wrap: wrap;
  width: 100%;
  justify-content: center;
}

.summary-card {
  flex: 1;
  min-width: 140px;
  background: #fff;
  border-radius: 12px;
  border: 1px solid #E5E7EB;
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
}

.summary-card-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.summary-card-content {
  display: flex;
  flex-direction: column;
}

.summary-card-count {
  font-size: 20px;
  font-weight: 700;
  line-height: 1;
  color: #111827;
}

.summary-card-label {
  font-size: 13px;
  color: #6B7280;
  margin-top: 4px;
  display: flex;
  align-items: center;
  gap: 4px;
}

/* SEARCH AREA */
.search-area {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.search-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 700;
  color: #111827;
}

.search-input-wrapper {
  position: relative;
  width: 300px;
}

.search-input-wrapper input {
  width: 100%;
  padding: 10px 10px 10px 36px;
  border-radius: 8px;
  border: 1px solid #E5E7EB;
  font-size: 14px;
  outline: none;
  background: #fff;
}

.search-input-wrapper input:focus {
  border-color: #0B6640;
}

.search-input-wrapper svg {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #9CA3AF;
}

/* ACCORDIONS */
.report-card {
  background: transparent;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.category-section {
  background: #fff;
  border-radius: 12px;
  border: 1px solid #E5E7EB;
  overflow: hidden;
  transition: all 0.2s ease;
}

.collapsed-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  cursor: pointer;
  background: #fff;
}

.collapsed-row:hover {
  background: #F9FAFB;
}

.cat-header-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.cat-icon-container {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.cat-title-wrap {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.category-label {
  font-size: 15px;
  font-weight: 600;
  color: #111827;
}

.category-subtitle {
  font-size: 13px;
  color: #6B7280;
}

.cat-header-right {
  display: flex;
  align-items: center;
  gap: 16px;
}

.cat-count {
  font-size: 14px;
  color: #6B7280;
  font-weight: 500;
}

.chevron-icon {
  color: #9CA3AF;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease;
}

.section-body {
  max-height: 0;
  opacity: 0;
  padding: 0;
  overflow: hidden;
  transition: max-height 0.35s ease, opacity 0.25s ease, padding 0.25s ease;
  border-top: 1px solid transparent;
}

.section-body.open {
  opacity: 1;
  padding: 0 20px 20px 20px;
  max-height: 800px;
  overflow-y: auto;
  border-top-color: #F3F4F6;
}

.section-body::-webkit-scrollbar { width: 4px; }
.section-body::-webkit-scrollbar-thumb { background: #c7c7c7; border-radius: 4px; }
.section-body::-webkit-scrollbar-track { background: transparent; }

/* TABLE STYLING */
.table-wrap {
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #E5E7EB;
  margin-top: 16px;
}

.purpose-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}

.purpose-table thead th {
  background: #F9FAFB;
  color: #4B5563;
  text-align: left;
  padding: 12px 16px;
  font-size: 12px;
  font-weight: 600;
  border-bottom: 1px solid #E5E7EB;
}

.purpose-table thead th:nth-child(1) { width: 15%; }
.purpose-table thead th:nth-child(2) { width: 18%; }
.purpose-table thead th:nth-child(3) { width: 40%; }
.purpose-table thead th:nth-child(4) { width: 15%; }
.purpose-table thead th:nth-child(5) { width: 12%; }

.purpose-table td {
  padding: 12px 16px;
  font-size: 13px;
  color: #111827;
  border-bottom: 1px solid #F3F4F6;
  word-break: break-word;
  white-space: normal;
}

.icon-btn {
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 6px;
  border-radius: 6px;
  color: #6B7280;
}

.icon-btn:hover { background: #F3F4F6; color: #111827; }

.purpose-vendor {
  font-size: 14px;
  font-weight: 600;
  margin: 16px 0 8px 0;
  color: #111827;
}

.flash {
  box-shadow: 0 0 0 2px #0B664033, 0 0 6px 2px #0B664066 inset;
  transition: box-shadow 0.3s ease;
}
`;

/* Format Date */
function formatDate(dateValue) {
  if (!dateValue) return "—";
  try {
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) return "—";
    if (date.getFullYear() < 1970) return "Session";
    const istFormatted = formatToIST(dateValue);
    if (istFormatted === "N/A") return "—";
    const datePart = istFormatted.split(" ")[0];
    return datePart.replace(/-/g, "/");
  } catch {
    return "—";
  }
}

/* Real Domain Helper */
function getRealDomain(c) {
  const d = c.domain?.toLowerCase();

  if (d === "first party" || d === "third party") {
    if (c.host) return c.host;

    if (c.url) {
      return c.url
        .replace(/https?:\/\//, "") // ← FIXED REGEX
        .split("/")[0];
    }

    return "Unknown Domain";
  }

  return c.domain || c.host || "Unknown Domain";
}


const Table = ({ rows = [], onEdit }) => (
  <div className="table-wrap">
    <table className="purpose-table">
      <thead>
        <tr>
          <th>Name</th>
          <th>Domain</th>
          <th>Description</th>
          <th>Expires</th>
          <th>Action</th>
        </tr>
      </thead>

      <tbody>
        {rows.length === 0 ? (
          <tr>
            <td colSpan="5" style={{ textAlign: "center", padding: 14 }}>
              No cookies found
            </td>
          </tr>
        ) : (
          rows.map((c, i) => (
            <tr key={i}>
              <td>{c.name}</td>
              <td>{getRealDomain(c)}</td>
              <td>{c.description || "—"}</td>
              <td>{formatDate(c.expires)}</td>
              <td>
                <button className="icon-btn" onClick={() => onEdit(c)}>
                  <Icon ic={<IcEditPen />} size="medium" />
                </button>
              </td>
            </tr>
          ))
        )}
      </tbody>
    </table>
  </div>
);

export default function ReportContainer({
  initialData = null,
  onEdit = null,
  highlightCategory = null,
  apiCategories = [],
}) {
  const [data, setData] = useState(initialData || null);

  const [open, setOpen] = useState({
    necessary: true,
    functional: false,
    analytics: false,
    advertisement: false,
    others: false,
  });

  /* Sync initial data */
  useEffect(() => {
    if (initialData) setData(initialData);
  }, [initialData]);

  /* Highlight category on navigate */
  useEffect(() => {
    if (!highlightCategory) return;
    const key = highlightCategory.toLowerCase();
    setOpen((prev) => ({ ...prev, [key]: true }));
    const el = document.querySelector(`#cat-${key}`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("flash");
      setTimeout(() => el.classList.remove("flash"), 2000);
    }
  }, [highlightCategory]);

  /* Standard category names (lowercased) – used to filter API categories */
  const STANDARD_CATS = useMemo(() => new Set([
    "necessary", "required", "essential",
    "functional",
    "analytics",
    "advertisement", "advertising",
    "others", "unclassified",
  ]), []);

  /* Build dynamic category entries from scan data + API categories list */
  const dynamicEntries = useMemo(() => {
    const entries = {};

    // 1. Add categories that already have cookies from the scan
    if (data?.dynamicCategories) {
      Object.entries(data.dynamicCategories).forEach(([label, arr]) => {
        const key = label.toLowerCase().replace(/\s+/g, "_").trim();
        entries[key] = { key, label: label.trim(), rows: arr || [] };
      });
    }

    // 2. Add custom categories from the API that are NOT one of the 5 standard ones
    if (Array.isArray(apiCategories)) {
      apiCategories.forEach((cat) => {
        const catName = (cat.category || "").trim();
        if (!catName) return;
        const catLower = catName.toLowerCase().trim();
        if (STANDARD_CATS.has(catLower)) return; // skip standard categories
        const key = catLower.replace(/\s+/g, "_");
        if (!entries[key]) {
          // Category from API with no cookies yet – show as empty section
          entries[key] = { key, label: catName, rows: [] };
        }
      });
    }

    return Object.values(entries);
  }, [data, apiCategories, STANDARD_CATS]);

  /* Count per category */
  const counts = useMemo(() => {
    const result = {
      necessary: Object.values(data?.required || {}).reduce(
        (s, arr) => s + (Array.isArray(arr) ? arr.length : 0),
        0
      ),
      functional: (data?.functional || []).length,
      analytics: (data?.analytics || []).length,
      advertisement: (data?.advertising || []).length,
      others: (data?.unclassified || []).length,
    };
    dynamicEntries.forEach(({ key, rows }) => {
      result[key] = (rows || []).length;
    });
    return result;
  }, [data, dynamicEntries]);

  /* All category sections: standard + dynamic */
  const allSections = useMemo(() => [
    { key: "necessary", label: "Necessary", rows: data?.required || {} },
    { key: "functional", label: "Functional", rows: data?.functional || [] },
    { key: "analytics", label: "Analytics", rows: data?.analytics || [] },
    { key: "advertisement", label: "Advertisement", rows: data?.advertising || [] },
    ...dynamicEntries,
    { key: "others", label: "Others", rows: data?.unclassified || [] },
  ], [data, dynamicEntries]);

  const toggle = (k) => setOpen((prev) => ({ ...prev, [k]: !prev[k] }));

  const handleEdit = (cookie) => onEdit && onEdit(cookie);

  const [search, setSearch] = useState("");

  const filteredSections = useMemo(() => {
    if (!search.trim()) return allSections.map(s => ({ ...s, filteredRows: s.rows }));
    const lowerSearch = search.toLowerCase();
    
    return allSections.map(section => {
      let matchedRows = section.rows;
      if (Array.isArray(section.rows)) {
        matchedRows = section.rows.filter(c => 
          (c.name || "").toLowerCase().includes(lowerSearch) || 
          (c.domain || "").toLowerCase().includes(lowerSearch)
        );
      } else {
        matchedRows = {};
        Object.entries(section.rows).forEach(([vendor, cookies]) => {
          const matched = cookies.filter(c => 
            (c.name || "").toLowerCase().includes(lowerSearch) || 
            (c.domain || "").toLowerCase().includes(lowerSearch)
          );
          if (matched.length > 0) matchedRows[vendor] = matched;
        });
      }
      return { ...section, filteredRows: matchedRows };
    });
  }, [allSections, search]);

  const totalCookies = Object.values(counts).reduce((a, b) => a + b, 0);

  const getCategoryMeta = (key) => {
    switch (key) {
      case "necessary": return { color: "#0B6640", bg: "#E6F5EC", icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path><polyline points="9 12 11 14 15 10"></polyline></svg>, desc: "Essential for the website to function properly." };
      case "functional": return { color: "#2563EB", bg: "#EFF6FF", icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="3"></circle><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path></svg>, desc: "Enable enhanced functionality and personalisation." };
      case "analytics": return { color: "#D97706", bg: "#FEF3C7", icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="20" x2="18" y2="10"></line><line x1="12" y1="20" x2="12" y2="4"></line><line x1="6" y1="20" x2="6" y2="14"></line></svg>, desc: "Help us understand how visitors interact with our website." };
      case "advertisement": return { color: "#DC2626", bg: "#FEF2F2", icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 11l18-5v12L3 14v-3z"></path><path d="M11.6 16.8a3 3 0 1 1-5.8-1.6"></path></svg>, desc: "Used to deliver relevant advertisements." };
      default: return { color: "#7C3AED", bg: "#F5F3FF", icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7"></rect><rect x="14" y="3" width="7" height="7"></rect><rect x="14" y="14" width="7" height="7"></rect><rect x="3" y="14" width="7" height="7"></rect></svg>, desc: "Uncategorised cookies discovered on the website." };
    }
  };

  return (
    <div className="report-container-wrapper" style={{ padding: "0px 0px 24px 0px" }}>
      <style>{css}</style>

      {/* SUMMARY CARDS */}
      <div className="summary-cards-container">
        <div className="summary-card">
          <div className="summary-card-icon" style={{ backgroundColor: "#E6F5EC", color: "#0B6640" }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>
          </div>
          <div className="summary-card-content">
            <span className="summary-card-count">{totalCookies}</span>
            <span className="summary-card-label">Total cookies <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="16" x2="12" y2="12"></line><line x1="12" y1="8" x2="12.01" y2="8"></line></svg></span>
          </div>
        </div>

        {allSections.filter(s => s.key !== "others").slice(0,4).map(s => {
          const meta = getCategoryMeta(s.key);
          return (
            <div className="summary-card" key={`card-${s.key}`}>
              <div className="summary-card-icon" style={{ backgroundColor: meta.bg, color: meta.color }}>
                {meta.icon}
              </div>
              <div className="summary-card-content">
                <span className="summary-card-count">{counts[s.key] || 0}</span>
                <span className="summary-card-label">{s.label} <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="16" x2="12" y2="12"></line><line x1="12" y1="8" x2="12.01" y2="8"></line></svg></span>
              </div>
            </div>
          )
        })}
      </div>

      <div className="search-area">
        <div className="search-title">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="12 2 2 7 12 12 22 7 12 2"></polygon><polyline points="2 12 12 17 22 12"></polyline><polyline points="2 17 12 22 22 17"></polyline></svg>
          Cookies by Category
        </div>
        <div className="search-input-wrapper">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
          <input 
            type="text" 
            placeholder="Search cookies..." 
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      <div className="center-wrap">
        <div className="report-card">
          {filteredSections.map(({ key, label, filteredRows }) => {
            const meta = getCategoryMeta(key);
            
            // Calculate active count based on filteredRows
            let activeCount = 0;
            if (Array.isArray(filteredRows)) {
              activeCount = filteredRows.length;
            } else {
              activeCount = Object.values(filteredRows).reduce((acc, arr) => acc + arr.length, 0);
            }

            return (
              <div key={key} id={`cat-${key}`} className="category-section">
                {/* Header */}
                <div className="collapsed-row" onClick={() => toggle(key)}>
                  <div className="cat-header-left">
                    <div className="cat-icon-container" style={{ backgroundColor: meta.bg, color: meta.color }}>
                      {meta.icon}
                    </div>
                    <div className="cat-title-wrap">
                      <span className="category-label">{label}</span>
                      <span className="category-subtitle">{meta.desc}</span>
                    </div>
                  </div>
                  <div className="cat-header-right">
                    <span className="cat-count">{activeCount} cookies</span>
                    <div className="chevron-icon" style={{ transform: open[key] ? "rotate(180deg)" : "none" }}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 12 15 18 9"></polyline></svg>
                    </div>
                  </div>
                </div>

                {/* Body */}
                <div className={`section-body ${open[key] ? "open" : ""}`}>
                  {key === "necessary" && Object.keys(filteredRows).length > 0 ? (
                    Object.entries(filteredRows).map(([vendor, cookies], i) => (
                      <div key={i}>
                        <div className="purpose-vendor">
                          {cookies[0]?.domain || vendor}
                        </div>
                        <Table rows={cookies} onEdit={handleEdit} />
                      </div>
                    ))
                  ) : (
                    <Table
                      rows={Array.isArray(filteredRows) ? filteredRows : []}
                      onEdit={handleEdit}
                    />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
